<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { OrganizationInterface } from '@/types';
import { PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  organizations?: OrganizationInterface[];
}

const props = defineProps<OrganizationPropsInterface>();

const { organizations } = toRefs(props);

const buildOrganizations = computed(() => {
  return total.value <= 3
    ? organizations.value
    : [
        organizations.value.at(0),
        {
          id: null,
          name: '...',
          children: [],
        },
        organizations.value.at(-1),
      ];
});

const tooltip = computed<string>(() => organizations.value.map((item) => item.name).join(' / '));

const total = computed<number>(() => organizations.value.length);

const groupBy = (data, property) => {
  return data
    .reduce((acc, obj) => {
      const key = obj[property];

      if (!acc[key]) acc[key] = [];
      acc[key].push(obj.name);
      return acc;
    }, [])
    .filter(Boolean);
};

const showSeparator: boolean = (key) => buildOrganizations.value.length !== key + 1;

const showSubTag: boolean = (item, key) => buildOrganizations.value.length - 1 === key && item.children.length;

const subTag: string = () => {
  const last = buildOrganizations.value.length - 1;
  const items = buildOrganizations.value[last].children;
  const children = groupBy(items, 'parent_id');
  const list = children.map((item) => `<li>${item.join(' / ')}</li>`).join('');

  return `<ul>${list}</ul>`;
};

const subTagTotal: string = (item) => `${item.children.length} Sub-organizations`;
</script>

<template>
  <template v-for="(item, key) in buildOrganizations" :key="key">
    <RouterLink
      v-if="!!item.id && can([PERMISSIONS.ORGANIZATIONS_CONNECTIONS])"
      :to="`/organizations/${item.id}`"
      class="font-semibold"
      v-text="item.name"
    />
    <span v-else-if="!!item.id" class="font-semibold" v-text="item.name" />
    <span
      v-else
      v-tooltip.bottom="{ value: tooltip, fitContent: false, class: 'custom-tooltip' }"
      class="text-teal-600 font-bold cursor-pointer"
      v-text="item.name"
    />
    <span v-if="showSeparator(key)" class="font-bold" v-text="'&nbsp;/&nbsp;'" />
    <Tag
      v-if="showSubTag(item, key)"
      v-tooltip.bottom="{
        value: subTag(item),
        escape: true,
        fitContent: false,
        class: 'custom-tooltip',
      }"
      :value="subTagTotal(item)"
      class="ml-3 bg-gray-light font-normal cursor-pointer"
    />
  </template>
</template>

<style lang="scss">
.custom-tooltip .p-tooltip-text {
  width: 20rem;

  ul {
    margin: 0 -10px !important;
  }

  li {
    margin: 0 -6px !important;
  }
}
</style>
